
import { GeoJSON, GeoJsonProperties } from 'geojson';
import * as L from 'leaflet';
import { Layer, LeafletEvent } from 'leaflet';
import { Component, Prop, Vue } from 'vue-property-decorator';

@Component({ name: 'LeafletMapPrescription' })
export default class LeafletMapPrescription extends Vue {
  @Prop() map!: L.Map;
  @Prop() feature!: GeoJSON;
  @Prop() userProducts!: { [dataColumns: string]: { productName: string; dataColumn: string; unit: string } };

  mapPolygon = null as L.GeoJSON | null;
  infoPanel = null as any;

  destroyed() {
    this.mapPolygon?.remove();
  }

  mounted() {
    try {
      this.renderPolygon();
      this.initInfoPanel();
    } catch (e) {
      console.error('Failed to render prescription polygon from GeoJSON: ', e);
      console.log(this.feature);
    }
  }

  getColor(properties: GeoJsonProperties): string {
    // Temporarily pick the first color property
    for (const property in properties) {
      if (property.endsWith('Color')) {
        return properties[property];
      }
    }

    // Default color
    return '#FFFFFF';
  }

  renderPolygon() {
    this.mapPolygon = new L.GeoJSON(this.feature, {
      style: this.renderStyle,
      onEachFeature: this.onEachFeature
    });
    this.mapPolygon.addTo(this.map).bringToFront();
    this.map.fitBounds(this.mapPolygon.getBounds());
  }

  initInfoPanel() {
    this.infoPanel = L.control();

    this.infoPanel.onAdd = function () {
      const panelRoot = L.DomUtil.create('div', 'presc-tooltip legend');
      const header = document.createElement('h4');
      header.textContent = 'Prescription Rate';
      panelRoot.append(header);
      const content = document.createElement('div');
      content.setAttribute('data-content', '');
      panelRoot.append(content);
      this.contentDiv = panelRoot;
      this.update();
      return this.contentDiv;
    };

    this.infoPanel.update = function (
      props: { [key: string]: any } | null = null,
      userProducts: {
        [dataColumn: string]: { productName: string; dataColumn: string; unit: string };
      }
    ) {
      const panelRoot: HTMLDivElement = this.contentDiv;
      const content = panelRoot.querySelector('[data-content]');
      if (!content) return;

      if (!props) {
        content.textContent = 'Error while fetching details';
      } else {
        content.innerHTML = '';
        // Populate content with color icon, key, value
        for (const prop in props) {
          // Map each data column name to user-defined product name
          let productName: string = prop;
          let unit = '';
          if (userProducts[prop]) {
            productName = userProducts[prop].productName;
            unit = userProducts[prop].unit;
          }
          // Every numerical data column should have a <name>Color property
          if (props[prop + 'Color'] !== undefined) {
            const icon = document.createElement('i');
            icon.style.background = props[prop + 'Color'];
            icon.setAttribute('data-content', '');
            content.append(icon);

            const value = document.createElement('span');
            value.textContent = `${productName}: ${props[prop]}${unit}`;
            value.setAttribute('data-content', '');
            content.append(value);

            content.append(document.createElement('br'));
          }
        }
      }
    };
  }

  renderStyle(feature: any) {
    return {
      fillColor: this.getColor(feature.properties),
      weight: 2,
      opacity: 1,
      color: 'white',
      dashArray: '3',
      fillOpacity: 0.7
    };
  }

  highlightFeature(e: LeafletEvent) {
    const layer = e.target;

    layer.setStyle({
      weight: 5,
      dashArray: '3',
      fillOpacity: 0.9
    });

    this.infoPanel.addTo(this.map);
    this.infoPanel.update(layer.feature.properties, this.userProducts);

    if (!L.Browser.ie && !L.Browser.opera && !L.Browser.edge) {
      layer.bringToFront();
    }
  }

  resetHighlight(e: LeafletEvent) {
    if (this.mapPolygon) {
      this.mapPolygon.resetStyle(e.target);
    }

    this.infoPanel.remove();
  }

  zoomToFeature(e: LeafletEvent) {
    this.map.fitBounds(e.target.getBounds());
  }

  onEachFeature(feature: any, layer: Layer) {
    layer.on({
      mouseover: this.highlightFeature,
      mouseout: this.resetHighlight,
      click: this.zoomToFeature
    });
  }
}
